import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from './Header';

const NgatVerificationPage = () => {
  const { userToken } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchResultByToken = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!userToken) {
          setError('Invalid verification link');
          return;
        }

        const response = await fetch('https://eap.ethernet.edu.et/api/ngat-exam-result-by-token', {
          method: 'POST',
          headers: {
            'X-API-Key': 'EAP-2024-7X9Y-2Z3W-4V5U-6T7S-8R9Q-0P1O',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_token: userToken
          })
        });

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Certificate not found or invalid token');
          } else if (response.status === 401) {
            throw new Error('Invalid API key');
          } else {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
        }

        const data = await response.json();
        
        if (data.status === 'success' && data.data) {
          setResult(data.data);
        } else {
          throw new Error(data.message || 'Failed to fetch result');
        }
      } catch (err) {
        console.error('Error fetching result by token:', err);
        setError(err.message || 'Failed to load certificate data');
      } finally {
        setLoading(false);
      }
    };

    fetchResultByToken();
  }, [userToken]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading certificate...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center p-8 bg-white rounded-2xl shadow-lg max-w-md w-full mx-4">
            <div className="text-red-500 text-6xl mb-4">
              <i className="fa-solid fa-exclamation-triangle"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Error</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Go Back Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center p-8 bg-white rounded-2xl shadow-lg max-w-md w-full mx-4">
            <div className="text-gray-500 text-6xl mb-4">
              <i className="fa-solid fa-file-circle-question"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">No Certificate Found</h2>
            <p className="text-gray-600 mb-6">No certificate data available for this token</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Go Back Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const passMark = 50; // NGAT pass mark is 50%
  const isIncomplete = result.total_score === 'incomplete' || result.status === 'Incomplete';
  const percentageScore = isIncomplete ? 'Incomplete' : result.total_score;
  const isPassed = isIncomplete ? false : (percentageScore >= passMark);
  const status = isIncomplete ? 'Incomplete' : (result.status || (isPassed ? 'Pass' : 'Fail'));

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Back Navigation */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
          >
            <i className="fa-solid fa-arrow-left mr-2" style={{ color: 'rgb(18,82,166)' }}></i>
            <span>Back to Home</span>
          </button>
        </div>

        {/* Certificate Verified Card */}
        <section className="bg-green-50 border border-green-200 rounded-xl p-6 mb-8">
          <div className="flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fa-solid fa-check text-2xl text-green-500"></i>
              </div>
              <h3 className="text-lg font-semibold text-green-800 mb-2">Certificate Verified</h3>
              <p className="text-green-700 text-sm">
                This certificate has been successfully verified through the official Ministry of Education portal.
              </p>
            </div>
          </div>
        </section>

        {/* Student Profile Card */}
        <section className="bg-white rounded-2xl shadow-lg mb-8 p-6 sm:p-8 border border-gray-100" style={{ fontFamily: 'Roboto, "Helvetica Neue", Arial, sans-serif' }}>
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left Side - Student Photo */}
            <div className="flex justify-center lg:justify-start lg:w-80">
              <div className="w-40 h-40 rounded-full overflow-hidden border-4 border-blue-200 shadow-lg">
                {result.photo_url ? (
                  <img 
                    src={`https://ngat.ethernet.edu.et/${result.photo_url}`} 
                    alt={`${result.student_name || 'Student'} Photo`}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className={`w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center ${result.photo_url ? 'hidden' : 'flex'}`}
                >
                  <i className="fa-solid fa-user text-5xl text-gray-600"></i>
                </div>
              </div>
            </div>
            
            {/* Right Side - Student Basic Info Cards */}
            <div className="flex-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { 
                    icon: 'fa-id-card', 
                    label: 'Full Name', 
                    value: result.student_name || 'N/A',
                    color: 'text-gray-800',
                    bgColor: 'from-blue-50 to-blue-100'
                  },
                  { 
                    icon: 'fa-user', 
                    label: 'Username', 
                    value: result.student_username || 'N/A',
                    color: 'text-gray-800',
                    bgColor: 'from-blue-50 to-blue-100'
                  },
                  { 
                    icon: 'fa-venus-mars', 
                    label: 'Gender', 
                    value: result.gender || 'N/A',
                    color: 'text-gray-800',
                    bgColor: 'from-blue-50 to-blue-100'
                  },
                  { 
                    icon: 'fa-calendar', 
                    label: 'Exam Date', 
                    value: result.exam_date || 'N/A',
                    color: 'text-gray-800',
                    bgColor: 'from-blue-50 to-blue-100'
                  },
                ].map(({ icon, label, value, color, bgColor }) => (
                  <div key={label} className={`p-4 rounded-xl bg-gradient-to-br ${bgColor} border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200`}>
                    <div className="flex items-center mb-2">
                      <i className={`fa-solid ${icon} mr-2 ${color}`} />
                      <p className="text-sm font-medium text-gray-600">{label}</p>
                    </div>
                    <p className={`font-semibold text-lg ${color} break-words`}>{value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Results Table */}
        <section className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 mb-8">
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr style={{ backgroundColor: 'rgb(18,82,166)' }}>
                  <th className="px-6 sm:px-8 py-4 sm:py-5 text-left text-sm font-bold text-white uppercase tracking-wider border-b border-white border-opacity-20">Total Score</th>
                  <th className="px-6 sm:px-8 py-4 sm:py-5 text-left text-sm font-bold text-white uppercase tracking-wider border-b border-white border-opacity-20">Pass Mark</th>
                  <th className="px-6 sm:px-8 py-4 sm:py-5 text-left text-sm font-bold text-white uppercase tracking-wider border-b border-white border-opacity-20">Status</th>
                  <th className="px-6 sm:px-8 py-4 sm:py-5 text-left text-sm font-bold text-white uppercase tracking-wider border-b border-white border-opacity-20">Certificate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr className="hover:bg-gray-50 transition-colors duration-200">
                  <td className="px-6 sm:px-8 py-4 sm:py-6 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="text-sm font-medium text-gray-900">
                        {percentageScore}%
                      </div>
                      <div className="ml-3 w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${
                            isIncomplete ? 'bg-yellow-500' : (isPassed ? 'bg-green-600' : 'bg-red-600')
                          }`}
                          style={{ width: `${isIncomplete ? 0 : (percentageScore || 0)}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-6 sm:px-8 py-4 sm:py-6 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {passMark}%
                    </div>
                  </td>
                  <td className="px-6 sm:px-8 py-4 sm:py-6 whitespace-nowrap">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      isIncomplete
                        ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                        : isPassed 
                        ? 'bg-green-50 text-green-700 border border-green-200' 
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`}>
                      <i className={`fa-solid ${isIncomplete ? 'fa-clock' : (isPassed ? 'fa-check-circle' : 'fa-times-circle')} mr-1`}></i>
                      {status}
                    </span>
                  </td>
                  <td className="px-6 sm:px-8 py-4 sm:py-6 whitespace-nowrap">
                    {isPassed && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                        <i className="fa-solid fa-check-circle mr-1"></i>
                        Verified
                      </span>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden">
            <div className="p-4 sm:p-6">
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-500">Total Score</span>
                    <span className="text-lg font-bold" style={{ color: isIncomplete ? '#d97706' : (isPassed ? '#059669' : '#dc2626') }}>
                      {percentageScore}%
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full ${
                        isPassed ? 'bg-green-600' : 'bg-red-600'
                      }`}
                      style={{ width: `${isIncomplete ? 0 : (percentageScore || 0)}%` }}
                    />
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-500">Pass Mark</span>
                    <span className="text-lg font-bold text-gray-700">
                      {passMark}%
                    </span>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-500">Status</span>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      isIncomplete
                        ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                        : isPassed 
                        ? 'bg-green-50 text-green-700 border border-green-200' 
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`}>
                      <i className={`fa-solid ${isIncomplete ? 'fa-clock' : (isPassed ? 'fa-check-circle' : 'fa-times-circle')} mr-1`}></i>
                      {status}
                    </span>
                  </div>
                </div>

                {isPassed && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-500">Certificate</span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                        <i className="fa-solid fa-check-circle mr-1"></i>
                        Verified
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

      </main>
    </div>
  );
};

export default NgatVerificationPage;
